import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Trash2, 
  FileSpreadsheet, 
  X, 
  MoreHorizontal,
  CirclePlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BulkActionsToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onAddToPortfolio: () => void;
  onBulkExport: () => void;
  onBulkDelete: () => void;
  canDelete?: boolean;
  deleteDisabledReason?: string;
  // Optional: enable cross-page selection of all filtered assets
  onSelectAllFiltered?: () => void;
  totalFilteredCount?: number;
}

export function BulkActionsToolbar({
  selectedCount,
  onClearSelection,
  onAddToPortfolio,
  onBulkExport,
  onBulkDelete,
  canDelete = true,
  deleteDisabledReason,
  onSelectAllFiltered,
  totalFilteredCount,
}: BulkActionsToolbarProps) {
  if (selectedCount === 0) return null;

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-md px-3 py-2 mb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-primary/15 text-primary text-xs">
            {selectedCount} asset{selectedCount !== 1 ? 's' : ''} selected
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
        
        <div className="flex items-center gap-1">
          {onSelectAllFiltered && typeof totalFilteredCount === 'number' && totalFilteredCount > selectedCount && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelectAllFiltered}
              className="h-7 px-2 text-xs"
            >
              Select all {totalFilteredCount}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onAddToPortfolio}
            className="h-7 px-2 text-xs gap-1"
          >
            <CirclePlus className="h-3 w-3" />
            Add to Portfolio
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1">
                <MoreHorizontal className="h-3 w-3" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onBulkExport}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canDelete ? (
                <DropdownMenuItem 
                  onClick={onBulkDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <DropdownMenuItem 
                          disabled
                          className="text-muted-foreground cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{deleteDisabledReason || 'Cannot delete selected assets'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
